import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";

import IconDelete from "@floji-internal/mui-icons/Delete";
import IconPause from "@floji-internal/mui-icons/Pause";
import IconPlay from "@floji-internal/mui-icons/PlayArrow";
import IconRestartAlt from "@floji-internal/mui-icons/RestartAlt";

import { CemTimedAction } from "--root/static_types/cem.types";
import { Strings } from "--root/strings/en";
import { getCemJobActionIntervalTimes } from "--root/utils/cem";
import { Tooltip } from "--components/tooltip";
import { Body2 } from "--components/typography";
import { SwaggerMutateClient } from "--client/common/apis/rest";
import { Button } from "--floponents-web/atoms/Button";
import { CircularTimer } from "--floponents-web/atoms/Timer/CircularTimer";
import { useToastErrorHandler } from "--floponents-web/molecules/ToastNotification/ToastProvider";

import { CemTimeline } from "./CemTimeline";
import { cemActionLabelMap, getCemActionColorMap } from "./utils";
import { WorkflowJobCem } from "--client/../static_types/workflow-service.types";

const CemStatus = styled.div`
    align-items: center;
    gap: ${props => props.theme.spacing(2)};
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
`;
const CemStatusTimer = styled.div`
    align-items: center;
    display: flex;
    gap: ${props => props.theme.spacing(2)};
`;
const CemStatusTimerNextActionType = styled.span`
    ${props => props.theme.typography.body1};
    color: ${props => props.theme.palette.text.primary};
`;
const CemStatusTimeline = styled(CemTimeline)`
    white-space: nowrap;
`;
const CemStatusControls = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing(1)};
    justify-content: center;
    width: 100%;
`;
const CemStatusControlsGroup = styled.div`
    align-items: center;
    background-color: ${props => props.theme.palette.background};
    border-radius: ${props => props.theme.radius.large};
    display: flex;
    gap: ${props => props.theme.spacing(0.5)};
    padding: ${props => props.theme.spacing(0.5)};
`;
const CemStatusControlsSeparator = styled.div`
    background-color: ${props => props.theme.palette.ui.border};
    height: 50%;
    width: 1px;
`;

enum CemUserAction { Suspend, Resume, Reset, Detach }

export interface CemStatusDialogBodyJob {
    cemJob: WorkflowJobCem,
    streamId: string;
    mutable: boolean;
}

export const CemStatusDialogBodyJob = (props: CemStatusDialogBodyJob) => {
    const { cemJob } = props;
    const err = useToastErrorHandler();
    const theme = useTheme();
    const cemActionTimes = useMemo(
        () => (cemJob ? getCemJobActionIntervalTimes(cemJob) : undefined),
        [cemJob]
    );

    let nextAction: CemTimedAction | undefined;
    if (cemJob?.status.actionIndex !== undefined) {
        nextAction = cemJob.actions[cemJob.status.actionIndex];
    }

    const actionColor = useMemo(() => (
        nextAction
            ? getCemActionColorMap(theme)[nextAction.type]
            : undefined
    ), [nextAction, theme]);

    const [performingUserAction, setPerfomingUserAction] = useState<CemUserAction>();

    const handleSuspend = useCallback(() => {
        setPerfomingUserAction(CemUserAction.Suspend);
        err(SwaggerMutateClient.SuspendCem({ streamId: props.streamId }))
            .catch(() => setPerfomingUserAction(undefined));
    }, [err, props.streamId]);

    const handleResume = useCallback(() => {
        setPerfomingUserAction(CemUserAction.Resume);
        err(SwaggerMutateClient.ResumeCem({ streamId: props.streamId }))
            .catch(() => setPerfomingUserAction(undefined));
    }, [err, props.streamId]);

    const handleReset = useCallback(() => {
        setPerfomingUserAction(CemUserAction.Reset);
        err(SwaggerMutateClient.ResetCem({ streamId: props.streamId }))
            .catch(() => setPerfomingUserAction(undefined));
    }, [err, props.streamId]);

    const handleDetach = useCallback(() => {
        setPerfomingUserAction(CemUserAction.Detach);
        err(SwaggerMutateClient.DetachCem({ streamId: props.streamId }))
            .catch(() => setPerfomingUserAction(undefined));
    }, [err, props.streamId]);

    useEffect(() => {
        setPerfomingUserAction(undefined);
    }, [cemJob])

    return (
        <CemStatus>
            {cemJob.status.type === "running" && nextAction && cemActionTimes && (
                <CemStatusTimer>
                    <Body2>
                        <CemStatusTimerNextActionType>
                            {cemActionLabelMap[nextAction.type]}
                        </CemStatusTimerNextActionType>{" "}
                        in:
                    </Body2>
                    <CircularTimer
                        size={100}
                        colorScheme={actionColor?.main}
                        startTimestamp={cemActionTimes[0]}
                        endTimestamp={cemActionTimes[1]}
                    />
                </CemStatusTimer>
            )}
            <CemStatusTimeline
                actions={cemJob.actions}
                backgroundColor={theme.palette.surface}
                showProgress
                suspendedAt={
                    cemJob.status.type === "suspended"
                        ? cemJob.status.suspendedAt
                        : undefined
                }
            />
            {
                props.mutable
                    ? <CemStatusControls>
                        <CemStatusControlsGroup>
                            {cemJob.status.type === "running" && <>
                                <Button
                                    color="warning"
                                    startIcon={<IconPause />}
                                    variant="text"
                                    onClick={handleSuspend}
                                    disabled={performingUserAction !== undefined}
                                    indeterminate={
                                        performingUserAction === CemUserAction.Suspend
                                    }
                                >
                                    {Strings.inline("Suspend")}
                                </Button>
                                <CemStatusControlsSeparator />
                            </>}
                            {cemJob.status.type === "suspended" && <>
                                <Button
                                    color="greyInverted"
                                    startIcon={<IconPlay />}
                                    variant="text"
                                    onClick={handleResume}
                                    disabled={performingUserAction !== undefined}
                                    indeterminate={
                                        performingUserAction === CemUserAction.Resume
                                    }
                                >
                                    {Strings.inline("Resume")}
                                </Button>
                                <CemStatusControlsSeparator />
                            </>}

                            <Button
                                color="danger"
                                startIcon={<IconRestartAlt />}
                                variant="text"
                                onClick={handleReset}
                                disabled={performingUserAction !== undefined}
                                indeterminate={
                                    performingUserAction === CemUserAction.Reset
                                }
                            >
                                {Strings.inline("Reset")}
                            </Button>

                            {cemJob.status.type !== "running" && <>
                                <CemStatusControlsSeparator />
                                <Tooltip tip={Strings.inline("Removes CEM from this stream.")}>
                                    <Button
                                        color="danger"
                                        startIcon={<IconDelete />}
                                        variant="text"
                                        onClick={handleDetach}
                                        disabled={performingUserAction !== undefined}
                                        indeterminate={
                                            performingUserAction === CemUserAction.Detach
                                        }
                                    >
                                        {Strings.inline("Detach")}
                                    </Button>
                                </Tooltip>
                            </>}
                        </CemStatusControlsGroup>
                    </CemStatusControls>
                    : null
            }
        </CemStatus>
    );
};